"use client";

import React, { useRef, useEffect, useState } from 'react';
import { Play, Pause } from 'lucide-react';

interface MovieTrailerProps {
  landscapeSrc?: string;
  portraitSrc?: string;
  landscapePoster?: string;
  portraitPoster?: string;
}

export function MovieTrailer({ landscapeSrc, portraitSrc, landscapePoster, portraitPoster }: MovieTrailerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showControlsTemporarily, setShowControlsTemporarily] = useState(false);
  const pausedTimeRef = useRef(0);

  // Don't render if no video sources provided
  if (!landscapeSrc && !portraitSrc) {
    return null;
  }

  const togglePlayback = async (startTime = 0) => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (video.paused) {
        if (startTime > 0) {
          video.currentTime = startTime;
        }
        await video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
      setHasUserInteracted(true);
    } catch (error) {
      console.error("Playback toggle error:", error);
    }

    if (isMobile) {
      setShowControlsTemporarily(true);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.setAttribute('playsinline', '');

    if (isMobile && !hasUserInteracted) {
      togglePlayback(0);
    } else if (!isMobile && !hasUserInteracted) {
      // Explicitly trigger loading so the video is buffered and ready for hover
      video.load();
    }
  }, [isMobile, hasUserInteracted]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showControlsTemporarily) {
      timer = setTimeout(() => {
        setShowControlsTemporarily(false);
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [showControlsTemporarily]);

  const handleMouseEnter = () => {
    const video = videoRef.current;
    if (!isMobile && video && video.paused) {
      togglePlayback(pausedTimeRef.current);
    }
  };

  const handleMouseLeave = () => {
    // Video continues playing after mouse leaves
  };

  // Use portrait video on mobile if available, otherwise fall back to landscape
  const currentSrc = isMobile
    ? (portraitSrc || landscapeSrc)
    : (landscapeSrc || portraitSrc);

  const currentPoster = isMobile
    ? (portraitPoster || landscapePoster)
    : (landscapePoster || portraitPoster);

  const videoAspectRatioClass = isMobile && portraitSrc ? 'aspect-[9/16]' : 'aspect-video';

  const isButtonVisible = isMobile
    ? (showControlsTemporarily || !isPlaying)
    : (!isPlaying);

  return (
    <div className="my-12 not-prose">
      <div
        className="relative overflow-hidden rounded-lg shadow-xl group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: 'pointer' }}
      >
        <video
          ref={videoRef}
          key={currentSrc}
          src={currentSrc}
          poster={currentPoster}
          loop
          muted
          playsInline
          controls
          preload="auto"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          className={`w-full h-auto object-cover -mt-[2px] ${videoAspectRatioClass}`}
        >
          Your browser does not support the video tag.
        </video>

        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <button
            className={`
              p-4 bg-black/50 rounded-full shadow-lg transition-opacity duration-300 pointer-events-auto cursor-pointer
              focus:outline-none focus:ring-2 focus:ring-white/70
              ${isButtonVisible ? 'opacity-100' : 'opacity-0'}
            `}
            aria-label={isPlaying ? "Pause Trailer" : "Play Trailer"}
            onClick={(e) => {
              e.stopPropagation();
              togglePlayback(0);
            }}
          >
            {isPlaying ? (
              <Pause className="text-white h-8 w-8 fill-white" />
            ) : (
              <Play className="text-white h-8 w-8 fill-white ml-0.5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
